import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Group, Modal, Select, Stack, Text, TextInput } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ApiError, officeApi } from '../api.js';
import {
  type NewVesselValues,
  newVesselSchema,
  type TemplateListState,
  templateField,
  timeZoneOptions,
} from '../fleet.js';
import { ErrorAlert } from './ErrorAlert.js';

interface Props {
  opened: boolean;
  onClose: () => void;
  /** Gemi eklendi: liste yenilenir, istenirse kurulum kodu açılır. */
  onCreated: (vessel: { id: string; name: string }) => void;
}

/**
 * "Gemi ekle" (mimari belge §8 "Kurulum devri"): gemi sunucusu etkinleşene kadar yazar buluttur.
 * Kurallar sunucudakiyle aynı şemadan; sunucu yine kendisi doğrular.
 */
export function AddVesselModal({ opened, onClose, onCreated }: Props) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const zones = useMemo(() => timeZoneOptions(browserZone), [browserZone]);
  const form = useForm<NewVesselValues>({
    resolver: zodResolver(newVesselSchema),
    defaultValues: { name: '', imo: '', timeZone: browserZone, docTemplateId: '' },
  });
  const { errors } = form.formState;
  // Doküman şablonu (ADR 0037 §4): pencere her açılışta şirkete açık şablonları okur
  const [templates, setTemplates] = useState<TemplateListState>({ kind: 'loading' });
  const field = templateField(templates);
  useEffect(() => {
    if (!opened) return;
    let live = true;
    setTemplates({ kind: 'loading' });
    officeApi.docTemplates().then(
      (list) => live && setTemplates({ kind: 'ready', ...list }),
      (failure) =>
        live &&
        setTemplates({
          kind: 'error',
          code: failure instanceof ApiError ? failure.code : 'GENERIC',
        }),
    );
    return () => {
      live = false;
    };
  }, [opened]);
  // Varsayılan önceden seçilir; kişi değiştirdiyse dokunulmaz
  const preferred = field.value;
  useEffect(() => {
    if (preferred && !form.getValues('docTemplateId')) form.setValue('docTemplateId', preferred);
  }, [preferred, form]);

  const close = () => {
    form.reset({ name: '', imo: '', timeZone: browserZone, docTemplateId: '' });
    setError(null);
    onClose();
  };

  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const vessel = await officeApi.createVessel(values);
      form.reset({ name: '', imo: '', timeZone: browserZone, docTemplateId: '' });
      onCreated(vessel);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'GENERIC');
    }
  });

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={t('fleet.addForm.title')}
      centered
      size="lg"
      radius="md"
      closeButtonProps={{ 'aria-label': t('fleet.closeDialog') }}
    >
      <Text fz="sm" c="dimmed" mb="lg">
        {t('fleet.addForm.description')}
      </Text>
      <ErrorAlert code={error} />
      <form onSubmit={submit} noValidate>
        <Stack gap="md">
          <TextInput
            label={t('fleet.addForm.name')}
            placeholder={t('fleet.addForm.namePlaceholder')}
            data-autofocus
            required
            withAsterisk={false}
            error={errors.name?.message ? t(errors.name.message) : undefined}
            {...form.register('name')}
          />
          <TextInput
            label={t('fleet.addForm.imo')}
            description={t('fleet.addForm.imoHint')}
            inputMode="numeric"
            maxLength={7}
            required
            withAsterisk={false}
            error={errors.imo?.message ? t(errors.imo.message) : undefined}
            {...form.register('imo')}
          />
          <Controller
            control={form.control}
            name="timeZone"
            render={({ field }) => (
              <Select
                label={t('fleet.addForm.timeZone')}
                description={t('fleet.addForm.timeZoneHint')}
                data={zones}
                searchable
                nothingFoundMessage="—"
                allowDeselect={false}
                comboboxProps={{ withinPortal: true }}
                aria-label={t('fleet.addForm.timeZone')}
                value={field.value}
                onChange={(value) => field.onChange(value ?? '')}
                onBlur={field.onBlur}
                error={errors.timeZone?.message ? t(errors.timeZone.message) : undefined}
              />
            )}
          />
          <Controller
            control={form.control}
            name="docTemplateId"
            render={({ field: input }) => (
              <Select
                label={t('fleet.addForm.template')}
                description={
                  field.options.length === 1
                    ? t('fleet.addForm.templateSingle')
                    : t('fleet.addForm.templateHint')
                }
                data={field.options}
                placeholder={
                  templates.kind === 'loading' ? t('fleet.addForm.templateLoading') : undefined
                }
                disabled={field.disabled}
                allowDeselect={false}
                comboboxProps={{ withinPortal: true }}
                value={input.value || field.value}
                onChange={(value) => input.onChange(value ?? '')}
                onBlur={input.onBlur}
                error={
                  field.error
                    ? t(`fleet.addForm.templateErrors.${field.error}`, {
                        defaultValue: t('fleet.addForm.templateErrors.GENERIC'),
                      })
                    : errors.docTemplateId?.message
                      ? t(errors.docTemplateId.message)
                      : undefined
                }
              />
            )}
          />
          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="default" onClick={close}>
              {t('fleet.addForm.cancel')}
            </Button>
            <Button
              type="submit"
              loading={form.formState.isSubmitting}
              disabled={!field.canSubmit}
              leftSection={<IconPlus size={18} />}
            >
              {t('fleet.addForm.submit')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
